import { CELL } from '../core/config';
import { createArchetype, createProp, riverX, seeded, type Archetype, type City } from './city';

/** Extend the existing town with connected, individually occupied outer parcels. */
export function expandRegion(city: City): City {
  if (city.regional) return city;
  city.regional = true;
  city.half = 630;
  city.logicalPopulation = 1200;
  city.ordinaryTraffic = 320;
  const random = seeded(city.seed + 904),
    graph = city.roadGraph!;
  let serial = Math.max(...[...city.buildings, ...city.props!, ...city.bridges!].map((b) => b.id)) + 1;
  const map = new Map(graph.nodes.map((n) => [`${n.x},${n.z}`, n.id]));
  const node = (x: number, z: number) => {
    const key = `${x},${z}`;
    let id = map.get(key);
    if (id === undefined) {
      id = graph.nodes.length;
      graph.nodes.push({ id, x, z });
      map.set(key, id);
    }
    return id;
  };
  const edge = (x: number, z: number, X: number, Z: number) => {
    if (Math.max(x, X) > 380 && Math.min(z, Z) < -290) return;
    const a = node(x, z),
      b = node(X, Z);
    if (graph.edges.some((e) => (e.from === a && e.to === b) || (e.from === b && e.to === a))) return;
    let bridgeId: number | undefined;
    if (z === Z && x < riverX(z) - 18 && X > riverX(z) + 18) {
      const bridge = createProp(serial++, riverX(z) - 17.5, z - 3.75, 'bridge');
      bridge.name = `Linden crossing ${Math.round(z)}`;
      city.bridges!.push(bridge);
      bridgeId = bridge.id;
    }
    graph.edges.push({
      id: graph.edges.length,
      from: a,
      to: b,
      width: Math.abs(z) > 500 || x > 340 ? 7.5 : 9.5,
      kind:
        bridgeId !== undefined
          ? 'bridge'
          : Math.abs(x) > 500 || Math.abs(z) > 500
            ? 'rural avenue'
            : 'boulevard',
      bridgeId,
    });
  };
  const xs = [-600, -510, -420, -330, -240, -190, -102, -14, 196, 260, 350, 440, 530, 620],
    zs = [-610, -510, -410, -310, -240, -192, -104, -16, 72, 160, 198, 250, 340, 430, 520, 610];
  for (const x of xs)
    for (let k = 0; k < zs.length - 1; k++) {
      const z = zs[k],
        Z = zs[k + 1];
      if (Math.abs(x) < 210 && z >= -210 && Z <= 210) continue;
      if (
        Math.abs(x) < 210 &&
        ((z < -210 && Z > -210) || (z < 210 && Z > 210)) &&
        !map.has(`${x},${Math.abs(z) < 210 ? z : Z}`)
      )
        continue;
      edge(x, z, x, Z);
    }
  for (const z of zs)
    for (let k = 0; k < xs.length - 1; k++) {
      const x = xs[k],
        X = xs[k + 1];
      if (Math.abs(z) < 210 && x >= -210 && X <= 210) continue;
      if (
        Math.abs(z) < 210 &&
        ((x < -210 && X > -210) || (x < 210 && X > 210)) &&
        !map.has(`${Math.abs(x) < 210 ? x : X},${z}`)
      )
        continue;
      edge(x, z, X, z);
    }
  city.districts!.push(
    { id: 'university', name: 'Westmere Campus', x: -410, z: -325, radius: 220 },
    { id: 'outer-residential', name: 'Orchard Neighbourhoods', x: -430, z: 160, radius: 235 },
    { id: 'industrial', name: 'Foundry & Logistics', x: -440, z: 465, radius: 180 },
    { id: 'commercial', name: 'Eastbank Exchange', x: 350, z: 120, radius: 185 },
    { id: 'airport', name: 'Alder Regional Airport', x: 470, z: -420, radius: 180 },
    { id: 'coast', name: 'Linden Coast', x: 310, z: 550, radius: 270 },
    { id: 'farmland', name: 'North Pastures', x: -90, z: -505, radius: 235 },
  );
  const district = (x: number, z: number) =>
    z > 480
      ? 'coast'
      : x > 300 && z < -300
        ? 'airport'
        : x > 210
          ? 'commercial'
          : z > 340
            ? 'industrial'
            : z < -430
              ? 'farmland'
              : z < -240
                ? 'university'
                : 'outer-residential';
  const families: Archetype[] = [
    'l-office',
    't-office',
    'twin-tower',
    'terraced-office',
    'slab-apartment',
    'stepped-apartment',
    'u-school',
    'sawtooth-factory',
    'hangar',
    'market-hall',
    'mall',
    'round-civic',
    'chapel',
    'grain-silo',
  ];
  const palettes = [0xdbc9b3, 0xc7d4ca, 0xd4b4a2, 0xb8c9cb, 0xd9d8c4, 0xc7b9a1];
  let plots = 0;
  for (let row = 0; row < zs.length - 1; row++)
    for (let col = 0; col < xs.length - 1; col++) {
      const x = xs[col],
        z = zs[row],
        w = xs[col + 1] - x,
        d = zs[row + 1] - z;
      if (w < 45 || d < 45 || (x >= -210 && xs[col + 1] <= 210 && z >= -210 && zs[row + 1] <= 210)) continue;
      // Continuous reserved railway corridor and coherent terminal/runway parcel.
      for (const [slot, ox, oz] of [
        [0, 12, 12],
        [1, w * 0.53, 12],
        [2, 12, d * 0.55],
      ] as const) {
        const bx = x + ox,
          bz = z + oz,
          cx = bx + 12,
          cz = bz + 10;
        if (Math.abs(cx) < 216 && Math.abs(cz) < 216) continue;
        if (cx > 330 && cx < 620 && cz < -290) continue;
        if (Math.abs(cx - riverX(cz)) < 40) continue;
        if ((Math.abs(cx + 270) < 28 || Math.abs(cx - 290) < 28) && cz > -300 && cz < 380) continue;
        if ((Math.abs(cz + 270) < 28 || Math.abs(cz - 350) < 28) && cx > -300 && cx < 320) continue;
        const zone = district(cx, cz);
        const options: Archetype[] =
          zone === 'outer-residential'
            ? ['pitched-house', 'semi-detached', 'slab-apartment', 'stepped-apartment', 'rowhouse']
            : zone === 'industrial'
              ? ['warehouse', 'sawtooth-factory', 'grain-silo']
              : zone === 'farmland'
                ? ['barn', 'pitched-house', 'grain-silo']
                : zone === 'coast'
                  ? ['market-hall', 'corner-shop', 'terraced-office', 'pitched-house']
                  : zone === 'university'
                    ? ['u-school', 'round-civic', 'slab-apartment', 'chapel']
                    : ['l-office', 't-office', 'twin-tower', 'terraced-office', 'mall', 'modern-apartment'];
        const kind =
          plots < families.length ? families[plots] : options[Math.floor(random() * options.length)];
        const wide = [
            'slab-apartment',
            'mall',
            'u-school',
            'market-hall',
            'twin-tower',
            'sawtooth-factory',
          ].includes(kind),
          bw = Math.min(wide ? 22 : 14, Math.floor((xs[col + 1] - bx - 9) / CELL)),
          bd = Math.min(kind === 'mall' ? 22 : 13, Math.floor((zs[row + 1] - bz - 9) / CELL));
        if (bw < 8 || bd < 8) continue;
        if (
          [...city.buildings, ...city.props!].some(
            (b) =>
              bx - 2 < b.x + b.w * CELL &&
              bx + bw * CELL + 2 > b.x &&
              bz - 3 < b.z + b.d * CELL &&
              bz + bd * CELL + 2 > b.z,
          )
        )
          continue;
        const floors =
          kind === 'grain-silo'
            ? 5
            : kind === 'twin-tower'
              ? 10
              : kind === 'hangar' || kind === 'sawtooth-factory'
                ? 1
                : kind === 'mall'
                  ? 3
                  : zone === 'commercial'
                    ? 3 + Math.floor(random() * 5)
                    : zone === 'farmland'
                      ? 1
                      : 2 + Math.floor(random() * 3);
        const b = createArchetype(
          serial++,
          bx,
          bz,
          kind,
          bw,
          bd,
          floors,
          palettes[plots % palettes.length],
          [0x5c888b, 0x4a6c83, 0x9c7655, 0x6e8268][plots % 4],
        );
        b.district = zone;
        b.chunk = `${Math.floor(bx / 60)},${Math.floor(bz / 60)}`;
        b.name = `${city.districts!.find((d) => d.id === zone)!.name} · ${kind.replaceAll('-', ' ')} ${plots + 1}`;
        city.buildings.push(b);
        plots++;
        const clearTree = (tx: number, tz: number) => {
          if (
            graph.edges.some((e) => {
              const a = graph.nodes[e.from],
                b = graph.nodes[e.to];
              return (
                tx > Math.min(a.x, b.x) - 6 &&
                tx < Math.max(a.x, b.x) + 6 &&
                tz > Math.min(a.z, b.z) - 6 &&
                tz < Math.max(a.z, b.z) + 6
              );
            })
          )
            return;
          const tree = createProp(serial++, tx, tz, 'tree', plots + slot);
          tree.district = zone;
          city.props!.push(tree);
        };
        clearTree(bx + bw * CELL + 3, bz + 4);
        if (slot === 0) {
          const bench = createProp(serial++, bx + 2, bz + bd * CELL + 3, 'bench');
          bench.district = zone;
          city.props!.push(bench);
        }
      }
    }
  // Keep the accepted parcel locations and identities, but compose the outer
  // housing as homes rather than another row of uniformly tall blocks. These
  // smaller footprints stay inside their already reserved lots; subsequent
  // interior/access generation uses the actual new floors and entrances.
  for (let i = 0; i < city.buildings.length; i++) {
    const old = city.buildings[i];
    if (
      old.district !== 'outer-residential' ||
      !['pitched-house', 'semi-detached', 'rowhouse'].includes(old.kind ?? '')
    )
      continue;
    const variant = old.id % 3,
      paired = old.kind === 'semi-detached',
      terrace = old.kind === 'rowhouse',
      width = Math.min(old.w, terrace ? 14 : paired ? 12 : 9 + variant * 2),
      depth = Math.min(old.d, terrace ? 10 : 9 + (old.id % 2) * 2),
      floors = terrace ? 2 + (old.id % 2) : paired ? 2 : 1 + Number(variant === 2),
      x = old.x + (old.w - width) * CELL * (variant === 0 ? 0.25 : 0.7),
      color = old.color;
    const home = createArchetype(
      old.id,
      x,
      old.z,
      old.kind as Archetype,
      width,
      depth,
      floors,
      color,
      [0x5c888b, 0x9c7655, 0x6e8268][variant],
    );
    city.buildings[i] = { ...old, ...home, name: old.name, district: old.district, chunk: old.chunk };
  }
  city.river = {
    width: 30,
    points: Array.from({ length: 85 }, (_, i) => ({ x: riverX(-630 + i * 15), z: -630 + i * 15 })),
  };
  return city;
}
